import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  SoftEnforcementSuggestion,
  SuggestionOutcome,
} from "@/data/contractor/networkExecution/softEnforcement/suggestions";

// ── DB row shape — matches soft_enforcement_suggestions table ─────────
interface SoftEnforcementSuggestionRow {
  id: string;
  kind: string;
  subject_id: string;
  subject_kind: string;
  rationale_key: string;
  suggested_at: string;
  payload_hash: string;
  outcome: string;
  outcome_at: string | null;
  outcome_actor_id: string | null;
  outcome_actor_role: string | null;
  outcome_reason: string | null;
  enforcement_mode_at_emit: string;
  expires_at: string;
  organization_id: string;
  created_at: string;
}

// ── Mappers ───────────────────────────────────────────────────────────
function toUiSuggestion(row: SoftEnforcementSuggestionRow): SoftEnforcementSuggestion {
  return {
    id: row.id,
    kind: row.kind as SoftEnforcementSuggestion["kind"],
    subjectId: row.subject_id,
    subjectKind: row.subject_kind as SoftEnforcementSuggestion["subjectKind"],
    rationaleKey: row.rationale_key,
    suggestedAt: row.suggested_at,
    payloadHash: row.payload_hash,
    outcome: row.outcome as SuggestionOutcome,
    outcomeAt: row.outcome_at ?? undefined,
    outcomeActorId: row.outcome_actor_id ?? undefined,
    outcomeActorRole: row.outcome_actor_role ?? undefined,
    outcomeReason: row.outcome_reason ?? undefined,
    enforcementModeAtEmit: row.enforcement_mode_at_emit as SoftEnforcementSuggestion["enforcementModeAtEmit"],
    expiresAt: row.expires_at,
  };
}

function toDbRow(
  suggestion: SoftEnforcementSuggestion,
  organizationId: string | null,
): Omit<SoftEnforcementSuggestionRow, "created_at"> {
  return {
    id: suggestion.id,
    kind: suggestion.kind,
    subject_id: suggestion.subjectId,
    subject_kind: suggestion.subjectKind,
    rationale_key: suggestion.rationaleKey,
    suggested_at: suggestion.suggestedAt,
    payload_hash: suggestion.payloadHash,
    outcome: suggestion.outcome,
    outcome_at: suggestion.outcomeAt ?? null,
    outcome_actor_id: suggestion.outcomeActorId ?? null,
    outcome_actor_role: suggestion.outcomeActorRole ?? null,
    outcome_reason: suggestion.outcomeReason ?? null,
    enforcement_mode_at_emit: suggestion.enforcementModeAtEmit,
    expires_at: suggestion.expiresAt,
    organization_id: organizationId as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────
export const SoftSuggestionService = {
  async getAll(): Promise<{ data: SoftEnforcementSuggestion[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("soft_enforcement_suggestions")
      .select("*")
      .order("suggested_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return {
      data: (data as SoftEnforcementSuggestionRow[]).map(toUiSuggestion),
      error: null,
    };
  },

  async getPending(): Promise<{ data: SoftEnforcementSuggestion[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("soft_enforcement_suggestions")
      .select("*")
      .eq("outcome", "pending")
      .order("suggested_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return {
      data: (data as SoftEnforcementSuggestionRow[]).map(toUiSuggestion),
      error: null,
    };
  },

  async insert(suggestion: SoftEnforcementSuggestion): Promise<{ data: SoftEnforcementSuggestion | null; error: unknown }> {
    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = toDbRow(suggestion, organizationId);

    const { data, error } = await supabase
      .from("soft_enforcement_suggestions")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[SoftSuggestionService.insert] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiSuggestion(data as SoftEnforcementSuggestionRow), error: null };
  },

  async upsertOutcome(
    id: string,
    outcome: Exclude<SuggestionOutcome, "pending">,
    actorId: string,
    actorRole: string,
    reason?: string,
  ): Promise<{ data: SoftEnforcementSuggestion | null; error: unknown }> {
    const patch: Record<string, unknown> = {
      outcome,
      outcome_at: new Date().toISOString(),
      outcome_actor_id: actorId,
      outcome_actor_role: actorRole,
      outcome_reason: reason ?? null,
    };

    const { data, error } = await supabase
      .from("soft_enforcement_suggestions")
      .update(patch)
      .eq("id", id)
      .select()
      .single();

    if (error || !data) {
      console.error("[SoftSuggestionService.upsertOutcome] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiSuggestion(data as SoftEnforcementSuggestionRow), error: null };
  },
};
